import { Sidebar } from "react-feather";
import { Theme } from "theme-ui";
import { darken, lighten } from "@theme-ui/color";

const theme: Theme = {
  breakpoints: ["768px", "1024px", "1280px"],
  space: {
    0: "0px", // 0
    px: "1px", // 1px
    0.5: "0.125rem", // 2px
    1: "0.25rem", // 4px
    1.5: "0.375rem", // 6px
    2: "0.5rem", // 8px
    2.5: "0.625rem", // 10px
    3: "0.75rem", // 12px
    3.5: "0.875rem", // 14px
    4: "1rem", // 16px
    5: "1.25rem", // 20px
    6: "1.5rem", // 24px
    7: "1.75rem", // 28px
    8: "2rem", // 32px
    9: "2.25rem", // 36px
    10: "2.5rem", // 40px
    12: "3rem", // 48px
    14: "3.5rem", // 56px
    16: "4rem", // 64px
    18: "4.5rem", // 72px
    20: "5rem", // 80px
    24: "6rem", // 96px
    28: "7rem", // 112px
    32: "8rem", // 128px
    36: "9rem", // 144px
    40: "10rem", // 160px
    44: "11rem", // 176px
    48: "12rem", // 192px
    56: "14rem", // 224px
    60: "15rem", // 240px
    64: "16rem", // 256px
    72: "18rem", // 288px
    80: "20rem", // 320px
    96: "24rem", // 384px
  },
  zIndices: {
    back: -2,
    mid: -1,
    front: 1,
    header: 2,
  },
  fonts: {
    heading: "Work Sans",
    body: "Bitter",
  },
  fontSizes: [
    "0.729rem", // 11.67px
    "0.833rem", // 13.33px
    "0.875rem", // 14px
    "1rem", // 16px
    "1.093rem", // 17.5px
    "1.25rem", // 20px
    "1.368rem", // 21.88px
    "1.563rem", // 25px
    "1.709rem", // 27.34px
    "2.016rem", // 32.25px
    "2.136rem", // 34.18px
    "2.441rem", // 39.06px
    "2.67rem", // 42.72px
    "3.052rem", // 48.83px
  ],
  fontWeights: {
    bold: 700,
    semiBold: 600,
    medium: 500,
    body: 400,
  },
  lineHeights: [
    "1.094rem", // 17.51px
    "1.249rem", // 19.99px
    "1.313rem", // 21px
    "1.5rem", // 24px
    "1.641rem", // 26.26px
    "1.875rem", // 30px
    "2.051rem", // 32.81px
    "2.344rem", // 37.5px
    "2.564rem", // 41.02px
    "2.929rem", // 46.87px
    "3.204rem", // 51.26px
    "3.663rem", // 58.6px
  ],
  colors: {
    themedBlue: "#214067",
    themedDarkBlue: "#122C4A",
    themedTeal: "#0C8888",
    themedBlack: "#25252B",
    themedCream: "#FAF2DB",
    warning: "#ffc107",
    success: "#198754",
    error: "#C6485A",
  },
  shadows: {
    homepageCard: "0px 0px 24px rgba(0, 0, 0, 0.25)",
  },
  sizes: {
    "1/2": "50%",
    "1/3": "33.333333%",
    "2/3": "66.666667%",
    "1/4": "25%",
    "2/4": "50%",
    "3/4": "75%",
    "1/5": "20%",
    "2/5": "40%",
    "3/5": "60%",
    "4/5": "80%",
    "1/6": "16.666667%",
    "2/6": "33.333333%",
    "3/6": "50%",
    "4/6": "66.666667%",
    "5/6": "83.333333%",
    "1/12": "8.333333%",
    "2/12": "16.666667%",
    "3/12": "25%",
    "4/12": "33.333333%",
    "5/12": "41.666667%",
    "6/12": "50%",
    "7/12": "58.333333%",
    "8/12": "66.666667%",
    "9/12": "75%",
    "10/12": "83.333333%",
    "11/12": "91.666667%",
    half: "50%",
    full: "100%",
    screenHeight: "100vh",
    screenWidth: "100vw",
    container: "1400px",
  },
  layout: {
    container: {
      px: 8,
      // py: 4,
    },
    wide: {
      variant: "layout.container",
      maxWidth: "1500px",
    },
    full: {
      variant: "layout.container",
      maxWidth: "100%",
    },
    narrow: {
      variant: "layout.container",
      maxWidth: "1300px",
    },
    thin: {
      variant: "layout.container",
      maxWidth: "1100px",
    },
    headerContainer: {
      variant: "layout.container",
      // maxWidth: ["374px", "728px", "784px", "1200px"],
      minHeight: ["80px", null, "140px"],
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    headerContainerAlt: {
      variant: "layout.altContainer",
      // maxWidth: ["374px", "728px", "784px", "1200px"],
      minHeight: ["80px", null, "140px"],
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
  },
  text: {
    paragraph: {
      fontFamily: "body",
      fontWeight: "body",
      fontSize: 3,
      lineHeight: 3,
      color: "themedBlack",
    },
    body: {
      fontFamily: "body",
      fontWeight: "body",
      fontSize: 3,
      lineHeight: 3,
      color: "themedBlack",
    },
    bodyTwo: {
      fontFamily: "body",
      fontWeight: "body",
      fontSize: [0, null, 1],
      lineHeight: [0, null, 1],
      color: "themedBlack",
    },
    h1: {
      fontSize: [12, null, 13],
      fontWeight: "bold",
      lineHeight: [10, null, 11],
    },
    h2: {
      fontSize: [10, null, 11],
      fontWeight: "semiBold",
      lineHeight: [8, null, 9],
    },
    h3: {
      fontSize: [8, null, 9],
      fontWeight: "medium",
      lineHeight: [6, null, 7],
    },
    h4: {
      fontSize: [6, null, 7],
      fontWeight: "medium",
      lineHeight: [4, null, 5],
    },
    h5: {
      fontSize: [4, null, 5],
      fontWeight: "medium",
      lineHeight: [2, null, 3],
    },
  },
  buttons: {
    primary: {
      fontFamily: "body",
      fontWeight: "body",
      bg: "themedTeal",
      color: "themedCream",
      px: 8,
      py: 3,
      borderRadius: "0 1.5em",
      border: "0.3em solid",
      borderColor: "themedTeal",
      textDecoration: "none",
      display: "inline-flex",
      alignItems: "center",
      cursor: "pointer",
      outline: "0.15em solid",
      outlineColor: "transparent",
      outlineOffset: "0em",
      ":hover": {
        backgroundColor: darken("themedTeal", 0.07),
        borderColor: darken("themedTeal", 0.07),
        outlineColor: "white",
        outlineOffset: "-0.4em",
      },
      transition: "all 0.2s",
      '&[aria-disabled="true"]': {
        backgroundColor: "gray",
        // outlineColor: "gray",
        borderColor: "gray",
        cursor: "not-allowed",
        "&:hover": {
          outlineColor: "transparent",
        },
        // pointerEvents: "none",
      },
    },
    small: {
      variant: "buttons.primary",
      px: 5,
      py: 1,
    },
    icon: {
      background: "none",
      color: "themedBlack",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      m: 0,
      p: 0,
      svg: {
        mr: 3,
        pr: 0,
      },
      textDecoration: "none",
      textUnderlineOffset: "0.3em",
      "&:hover": {
        textDecoration: "underline dotted",
      },
    },
    lightBg: {
      variant: "buttons.primary",
      ":hover": {
        cursor: "pointer",
        bg: "themedDarkBlue",
        color: "themedCream",
      },
    },
    lightBgSmall: {
      variant: "buttons.lightBg",
      fontSize: 1,
      px: 4,
    },
    text: {
      backgroundColor: "transparent",
      color: "black",
      p: 0,
      m: 0,
      border: "none",
      textDecoration: "underline",
      cursor: "pointer",
      "&:hover": {
        color: "themedTeal",
      },
    },
    pagination: {
      p: 0,
      fontFamily: "heading",
      fontWeight: "medium",
      fontSize: [5, null, 6],
      backgroundColor: "transparent",
      color: "themedBlack",
      ":hover": {
        cursor: "pointer",
        color: "themedTeal",
      },
      "::before": {
        content: `"<< "`,
      },
    },
    underline: {
      p: 0,
      fontFamily: "body",
      fontSize: "inherit",
      // fontWeight: "bold",
      textDecoration: "underline",
      color: "themedBlack",
      cursor: "pointer",
      border: "none",
      bg: "transparent",
      ":hover": {
        color: "themedTeal",
      },
    },
    underlineBold: {
      p: 0,
      fontFamily: "body",
      fontSize: [2, null, 3],
      fontWeight: "bold",
      textDecoration: "underline",
      color: "themedBlack",
      cursor: "pointer",
      border: "none",
      bg: "transparent",
      ":hover": {
        color: "themedTeal",
      },
    },
    dashboardSidebar: {
      display: "flex",
      alignItems: "center",
      p: 0,
      fontFamily: "heading",
      color: "#929295",
      cursor: "pointer",
      background: "transparent",
      ":hover, :focus, &.active": {
        color: "themedTeal",
        svg: {
          fill: "#0C8888",
        },
      },
      span: {
        mr: 3,
        display: "inline-block",
        verticalAlign: "middle",
      },
      svg: {
        fill: "#929295",
      },
    },
    credit: {
      input: {
        position: "fixed",
        opacity: 0,
        pointerEvents: "none",
      },
      ":hover label, input:checked + label": {
        backgroundColor: "themedDarkBlue",
        color: "themedCream",
      },
      " label, h4": {
        color: "inherit",
        width: "auto",
      },
      " label": {
        // width: "184px"
        px: 4,
        py: 5,
        height: "103px",
        fontFamily: "heading",
        color: "themedBlack",
        cursor: "pointer",
        border: "2px solid #214067",
        backgroundColor: "transparent",
        borderRadius: "30px 0",
        textAlign: "left",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        m: 0,
      },

      // label: {},
    },
    warning: {
      variant: "buttons.primary",
      color: "black",
      backgroundColor: "warning",
      borderColor: "warning",
    },
    dotted: {
      cursor: "pointer",
      backgroundColor: "transparent",
      borderRadius: 0,
      p: 0,
      textDecoration: "none",
      color: "inherit",
      borderBottom: "1px dotted black",
    },
  },
  links: {
    underlineBold: {
      fontFamily: "body",
      fontSize: [2, null, 3],
      fontWeight: "bold",
      textDecoration: "underline",
      whiteSpace: "nowrap",
      cursor: "pointer",
      ":hover": {
        color: "themedTeal",
      },
    },
    underline: {
      fontFamily: "body",
      fontSize: [1, null, 3],
      textDecoration: "underline",
      whiteSpace: "nowrap",
      ":hover": {
        cursor: "pointer",
        color: "themedTeal",
      },
    },
    black: {
      fontFamily: "body",
      fontWeight: "bold",
      color: "themedBlack",
      textDecoration: "underline",
      ":hover": {
        cursor: "pointer",
        color: "themedTeal",
      },
      ":hover + span": {
        bg: "themedTeal",
      },
    },
    reportReady: {
      fontFamily: "body",
      fontWeight: "bold",
      color: "themedBlack",
      textDecoration: "underline",
      cursor: "pointer",
      span: {
        ml: 3,
      },
    },
    header: {
      fontFamily: "body",
      fontSize: 3,
      cursor: "pointer",
    },
    footer: {
      fontFamily: "body",
      fontSize: [2, null, 3],
      color: "themedCream",
      cursor: "pointer",
    },
  },
  forms: {
    label: {
      mb: 2,
      fontFamily: "body",
      fontSize: [2, null, 3],
      color: "themedBlack",
    },
    input: {
      width: "100%",
      height: "43px",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "themedBlack",
      borderRadius: "0px",
      fontFamily: "body",
    },

    formikInput: {
      width: "100%",
      height: "43px",
      py: 2,
      px: 3,
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "themedBlack",
      borderRadius: "0px",
      fontFamily: "body",
    },
    checkbox: {
      width: ["11px", null, "13px"],
      height: ["11px", null, "13px"],
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "themedBlack",
      borderRadius: "0px",
      cursor: "pointer",
      ":checked": {
        bg: "themedTeal",
        borderColor: "themedTeal",
      },
      "+ label": {
        ml: 2,
        fontSize: [1, null, 3],
        cursor: "pointer",
        whiteSpace: "nowrap",
      },
      ":checked + label": {
        color: "themedTeal",
      },
    },
    textarea: {
      height: "134px",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "themedBlack",
      borderRadius: "0px",
      resize: "none",
    },
    reviewerNotes: {
      variant: "forms.textarea",
      width: "100%",
      height: "100px",
    },
  },
  styles: {
    root: {
      boxSizing: "border-box",
      body: {
        m: 0,
        p: 0,
      },
    },
    hr: {
      height: "2px",
      color: "rgb(37 37 43 / 0.5)",
    },
    progress: {
      width: ["80%", null, "100%"],
      height: "18px",
      color: "themedTeal",
      bg: "transparent",
      border: "1px solid #25252B",
      borderRadius: "0px",
    },
    srOnly: {
      display: "none",
    },
  },
};

export default theme;
